/**
 * Version-keyed cache over `parseStatementSegments`.
 *
 * The query editor resolves the statement under the cursor on every settled
 * caret move (selection handler) and on execute shortcuts (F5). Both need the
 * full buffer text plus its parsed statement segments — but the buffer only
 * changes when the user edits, not when the caret moves. Monaco bumps the
 * model version id on every edit and never on caret/selection moves, so one
 * `{ versionId, engineId }`-keyed entry lets caret navigation skip both the
 * O(document) `getValue()` string materialization AND the O(document)
 * statement parse entirely; only actual edits re-read and re-parse.
 */

import { useCallback, useRef } from 'react';
import type { RefObject } from 'react';
import type { DatabaseEngineId } from '@/core/engines';
import type { SqlMonacoHandle } from '../../common/utils/sqlMonacoTypes';
import { parseStatementSegments, type StatementSegment } from '../utils/statementAtCursor';

export type StatementSegmentsSnapshot = {
  /** The buffer text the segments were parsed from (current for the returned version). */
  sql: string;
  segments: StatementSegment[];
};

type CacheEntry = StatementSegmentsSnapshot & {
  versionId: number;
  engineId: DatabaseEngineId | undefined;
};

/**
 * Returns a stable getter that live-reads the editor's current statement
 * segments, served from cache while the model version (and engine) are
 * unchanged. Returns null when the editor isn't mounted. Never lags Monaco:
 * the version check makes a stale cache entry unreachable.
 */
export function useStatementSegments(
  editorRef: RefObject<SqlMonacoHandle | null>,
  engineId: DatabaseEngineId | undefined
): () => StatementSegmentsSnapshot | null {
  const cacheRef = useRef<CacheEntry | null>(null);

  return useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return null;

    const versionId = editor.getModelVersionId();
    const cached = cacheRef.current;
    if (
      versionId !== null &&
      cached !== null &&
      cached.versionId === versionId &&
      cached.engineId === engineId
    ) {
      return cached;
    }

    const sql = editor.getValue();
    if (sql === undefined) return null; // buffer unreadable (see SqlMonacoHandle.getValue)
    const segments = parseStatementSegments(sql, engineId);
    if (versionId === null) return { sql, segments };
    // Return the stored entry itself so reads of the same buffer version
    // share identity — callers may identity-compare snapshots.
    const entry: CacheEntry = { versionId, engineId, sql, segments };
    cacheRef.current = entry;
    return entry;
  }, [editorRef, engineId]);
}
